import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AuditStatusConfirmDialog from '../../components/shared/AuditStatusConfirmDialog';
import FormSection from '../../components/shared/FormSection';
import SuccessToast from '../../components/shared/SuccessToast';
import {
  canCreateAdminUsers,
  canManageAdminOrganizations,
  canManageAdminUsers,
  canViewAdminTenancy,
  canViewAdminUsers,
  getCurrentOrganizationIds,
  getCurrentUserRoles,
  getCurrentUserRole,
} from '../../helpers/authSession';
import {
  createAdminOrganization,
  deactivateAdminOrganization,
  getAdminOrganizations,
  updateAdminOrganization,
} from '../../services/complianceRules';
import {
  activateAccessManagementUser,
  createAccessManagementOrganization,
  createAccessManagementUser,
  getAccessManagementOrganizations,
  getAccessManagementUsers,
  setAccessManagementUserCredentials,
  suspendAccessManagementUser,
  updateAccessManagementOrganization,
  updateAccessManagementUser,
} from '../../services/accessManagementUsers';
import {
  createAccessManagementRole,
  deleteAccessManagementRole,
  getAccessManagementPermissions,
  getAccessManagementRoles,
  updateAccessManagementRole,
} from '../../services/accessManagementRoles';
import { asDisplayText, formatDateTimeHumanized } from '../../helpers/dateTimeFormat';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';

const CLIENT_ROLE_OPTIONS = ['ROOT', 'SUPERADMIN', 'ADMIN', 'AUDIT', 'MANAGER', 'ANALYST', 'MONITOR'];
const ROLE_RANK = {
  ROOT: 800,
  SUPERADMIN: 700,
  ADMIN: 600,
  MANAGER: 500,
  AUDIT: 400,
  ANALYST: 300,
  MONITOR: 100,
};
const TAB_VALUES = {
  organizations: 'organizations',
  users: 'users',
  roles: 'roles',
};
const PERMISSION_ACTION_ORDER = ['read', 'create', 'update', 'view', 'manage'];
const PERMISSION_ACTION_LABELS = {
  read: 'Ler',
  create: 'Criar',
  update: 'Atualizar',
  view: 'Ver',
  manage: 'Gerenciar',
};
const PERMISSION_RESOURCE_LABELS = {
  users: 'Usuários (AMS)',
  organizations: 'Organizações (AMS)',
  permissions: 'Catálogo de permissões (AMS)',
  dashboard: 'Dashboard',
  transactions: 'Transações',
  'transactions.details': 'Detalhes de transações',
  'transactions.status': 'Status de transações',
  'policy.rules': 'Regras da Policy',
  'admin.plans': 'Planos Base',
  tenancy: 'Organizações e Usuários (painel)',
  'tenancy.organizations': 'Gestão de organizações (painel)',
  'tenancy.clients': 'Gestão de clientes técnicos (painel)',
};
const ORGANIZATION_STATUS_LABELS = {
  active: 'Ativa',
  inactive: 'Inativa',
};
const CLIENT_STATUS_LABELS = {
  active: 'Ativo',
  suspended: 'Suspenso',
};
const CLIENT_ROLE_LABELS = {
  ROOT: 'Root',
  SUPERADMIN: 'Superadministrador',
  ADMIN: 'Administrador',
  AUDIT: 'Auditoria',
  MANAGER: 'Gestor',
  ANALYST: 'Analista',
  MONITOR: 'Monitor',
};
const resolveLatestChangeSummary = (entry) => {
  if (!entry) {
    return '-';
  }

  const changedBy = asDisplayText(entry.changed_by || entry.changedBy);
  const reason = asDisplayText(entry.reason);

  if (changedBy && reason) {
    return `${changedBy} - ${reason}`;
  }

  return changedBy || reason || '-';
};

const resolveInitialOrganizationForm = () => ({
  name: '',
});

const resolveInitialStatusChangeDialog = () => ({
  open: false,
  organization: null,
  reason: '',
});

const resolveInitialEditOrganizationDialog = () => ({
  open: false,
  organization: null,
  name: '',
});

const resolveInitialUserStatusDialog = () => ({
  open: false,
  user: null,
  targetStatus: '',
  reason: '',
});

const resolveInitialRoleStatusDialog = () => ({
  open: false,
  role: null,
  reason: '',
});

const resolveInitialAmsUserForm = () => ({
  externalId: '',
  name: '',
  email: '',
  role: 'MONITOR',
  organizationId: '',
  status: 'active',
});

const resolveInitialRoleForm = () => ({
  name: '',
  organizationId: '',
  permissionIds: [],
});

const generateRandomSecret = (length = 24) => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*_-+=';
  const randomValues = typeof crypto !== 'undefined' && crypto?.getRandomValues
    ? crypto.getRandomValues(new Uint32Array(length))
    : Array.from({ length }, () => Math.floor(Math.random() * 0x100000000));

  return Array.from(randomValues, (value) => alphabet[value % alphabet.length]).join('');
};

export default function AdminTenancy() {
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(TAB_VALUES.organizations);
  const [organizations, setOrganizations] = React.useState([]);
  const [amsUsers, setAmsUsers] = React.useState([]);
  const [amsOrganizations, setAmsOrganizations] = React.useState([]);
  const [amsRoles, setAmsRoles] = React.useState([]);
  const [amsPermissions, setAmsPermissions] = React.useState([]);
  const [organizationForm, setOrganizationForm] = React.useState(resolveInitialOrganizationForm);
  const [statusChangeDialog, setStatusChangeDialog] = React.useState(resolveInitialStatusChangeDialog);
  const [editOrganizationDialog, setEditOrganizationDialog] = React.useState(resolveInitialEditOrganizationDialog);
  const [userStatusDialog, setUserStatusDialog] = React.useState(resolveInitialUserStatusDialog);
  const [roleStatusDialog, setRoleStatusDialog] = React.useState(resolveInitialRoleStatusDialog);
  const [amsUserForm, setAmsUserForm] = React.useState(resolveInitialAmsUserForm);
  const [roleForm, setRoleForm] = React.useState(resolveInitialRoleForm);
  const [editingAmsUserId, setEditingAmsUserId] = React.useState('');
  const [editingRoleId, setEditingRoleId] = React.useState('');
  const [roleFilterOrganizationId, setRoleFilterOrganizationId] = React.useState('all');
  const [credentialsDialog, setCredentialsDialog] = React.useState({
    open: false,
    user: null,
    password: '',
  });
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [lastGeneratedSecret, setLastGeneratedSecret] = React.useState('');
  const [lastGeneratedAmsPassword, setLastGeneratedAmsPassword] = React.useState('');

  const currentUserRole = getCurrentUserRole();
  const currentUserRoles = getCurrentUserRoles();
  const effectiveRole = [...currentUserRoles, currentUserRole]
    .sort((left, right) => (ROLE_RANK[right] || 0) - (ROLE_RANK[left] || 0))[0];
  const isAdmin = ['ROOT', 'SUPERADMIN', 'ADMIN'].includes(effectiveRole);
  const isGlobalRole = ['ROOT', 'SUPERADMIN'].includes(effectiveRole);
  const isPolicyManager = effectiveRole === 'MANAGER';
  const creatableRoleOptions = CLIENT_ROLE_OPTIONS.filter(
    (role) => (ROLE_RANK[role] || 0) < (ROLE_RANK[effectiveRole] || 0),
  );
  // Papeis customizados nao tem rank numerico -- so quem gerencia o proprio
  // Role (ROOT/SUPERADMIN/ADMIN) pode atribui-los, mesma regra do backend
  // (RoleManagementPolicy#allowed?). Restrito a papeis ativos da organizacao
  // selecionada no formulario.
  const creatableUserRoleOptions = React.useMemo(() => {
    const customRoleNames = isAdmin
      ? amsRoles
        .filter((role) => role.active && !CLIENT_ROLE_OPTIONS.includes(role.name) && role.organizationId === amsUserForm.organizationId)
        .map((role) => role.name)
      : [];

    return [...creatableRoleOptions, ...customRoleNames];
  }, [amsRoles, amsUserForm.organizationId, creatableRoleOptions, isAdmin]);
  const canView = canViewAdminTenancy();
  const canManageOrganizations = canManageAdminOrganizations();
  const canViewUsersTab = canViewAdminUsers();
  const canCreateUsers = canCreateAdminUsers();
  const canManageUsersTab = canManageAdminUsers();

  const canManageTargetAmsUser = React.useCallback((user) => {
    if (isAdmin) {
      return true;
    }

    if (isPolicyManager) {
      return (ROLE_RANK[user.role] || 0) < ROLE_RANK.MANAGER;
    }

    return false;
  }, [isAdmin, isPolicyManager]);

  const loadData = React.useCallback(async () => {
    if (!canView) {
      setOrganizations([]);
      setAmsUsers([]);
      setAmsOrganizations([]);
      setAmsRoles([]);
      setAmsPermissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const [
        organizationsResponse,
        amsUsersResponse,
        amsOrganizationsResponse,
        amsRolesResponse,
        amsPermissionsResponse,
      ] = await Promise.all([
        getAdminOrganizations(),
        canViewUsersTab ? getAccessManagementUsers() : Promise.resolve([]),
        canViewUsersTab ? getAccessManagementOrganizations() : Promise.resolve([]),
        isAdmin ? getAccessManagementRoles() : Promise.resolve([]),
        isAdmin ? getAccessManagementPermissions() : Promise.resolve([]),
      ]);

      setOrganizations(organizationsResponse);
      setAmsUsers(amsUsersResponse || []);
      setAmsOrganizations(amsOrganizationsResponse || []);
      setAmsRoles(amsRolesResponse || []);
      setAmsPermissions(amsPermissionsResponse || []);
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
      setOrganizations([]);
      setAmsUsers([]);
      setAmsOrganizations([]);
      setAmsRoles([]);
      setAmsPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [canView, canViewUsersTab, isAdmin]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const resetOrganizationForm = React.useCallback(() => {
    setOrganizationForm(resolveInitialOrganizationForm());
  }, []);

  const resetAmsUserForm = React.useCallback(() => {
    const scopedOrganizationIds = getCurrentOrganizationIds();

    setAmsUserForm({
      ...resolveInitialAmsUserForm(),
      organizationId: !isGlobalRole && scopedOrganizationIds.length === 1 ? scopedOrganizationIds[0] : '',
    });
    setEditingAmsUserId('');
  }, [isGlobalRole]);

  const resetRoleForm = React.useCallback(() => {
    const scopedOrganizationIds = getCurrentOrganizationIds();

    setRoleForm({
      ...resolveInitialRoleForm(),
      organizationId: !isGlobalRole && scopedOrganizationIds.length === 1 ? scopedOrganizationIds[0] : '',
    });
    setEditingRoleId('');
  }, [isGlobalRole]);

  const permissionLabelById = React.useMemo(() => (
    amsPermissions.reduce((acc, permission) => {
      const resourceLabel = PERMISSION_RESOURCE_LABELS[permission.resource] || permission.resource;
      const actionLabel = PERMISSION_ACTION_LABELS[permission.action] || permission.action;
      acc[permission.id] = `${resourceLabel} (${actionLabel})`;
      return acc;
    }, {})
  ), [amsPermissions]);

  const renderPermissionChips = React.useCallback((role) => {
    const labels = role.permissionIds.map((id) => permissionLabelById[id]).filter(Boolean);

    if (!labels.length) {
      return <Typography variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>;
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
        {labels.map((label) => (
          <Chip key={label} size="small" label={label} variant="outlined" />
        ))}
      </Stack>
    );
  }, [permissionLabelById]);

  const permissionIdByResourceAction = React.useMemo(() => (
    amsPermissions.reduce((acc, permission) => {
      acc[`${permission.resource}:${permission.action}`] = permission.id;
      return acc;
    }, {})
  ), [amsPermissions]);

  const permissionMatrixResources = React.useMemo(() => (
    Array.from(new Set(amsPermissions.map((permission) => permission.resource))).sort((left, right) => (
      (PERMISSION_RESOURCE_LABELS[left] || left).localeCompare(PERMISSION_RESOURCE_LABELS[right] || right)
    ))
  ), [amsPermissions]);

  const permissionMatrixActions = React.useMemo(() => {
    const present = new Set(amsPermissions.map((permission) => permission.action));
    return PERMISSION_ACTION_ORDER.filter((action) => present.has(action));
  }, [amsPermissions]);

  const defaultRoles = React.useMemo(() => amsRoles.filter((role) => role.isDefault), [amsRoles]);
  const customRoles = React.useMemo(() => amsRoles.filter((role) => !role.isDefault), [amsRoles]);

  // Papeis padrao sao identicos em toda organizacao (mesmo catalogo estatico) --
  // sem filtro, um ator global (ROOT/SUPERADMIN) veria os mesmos 7 nomes
  // duplicados uma vez por organizacao. O filtro deixa a listagem por uma
  // organizacao de cada vez, mesmo padrao ja usado na aba Clientes tecnicos.
  const filteredDefaultRoles = React.useMemo(() => (
    roleFilterOrganizationId === 'all'
      ? defaultRoles
      : defaultRoles.filter((role) => role.organizationId === roleFilterOrganizationId)
  ), [defaultRoles, roleFilterOrganizationId]);

  const filteredCustomRoles = React.useMemo(() => (
    roleFilterOrganizationId === 'all'
      ? customRoles
      : customRoles.filter((role) => role.organizationId === roleFilterOrganizationId)
  ), [customRoles, roleFilterOrganizationId]);

  const amsOrganizationNameById = React.useMemo(() => (
    amsOrganizations.reduce((acc, organization) => {
      acc[organization.id] = organization.name;
      return acc;
    }, {})
  ), [amsOrganizations]);

  const availableAmsOrganizations = React.useMemo(() => {
    if (isGlobalRole) {
      return amsOrganizations;
    }

    const scopedOrganizationIds = getCurrentOrganizationIds();
    return amsOrganizations.filter((organization) => scopedOrganizationIds.includes(organization.id));
  }, [amsOrganizations, isGlobalRole]);

  const handleTabChange = React.useCallback((event, nextTab) => {
    setActiveTab(nextTab);
    setErrorMessage('');
    setSuccessMessage('');
    setLastGeneratedSecret('');
    setLastGeneratedAmsPassword('');
  }, []);

  const handleOrganizationField = React.useCallback((field, value) => {
    setOrganizationForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleAmsUserField = React.useCallback((field, value) => {
    setAmsUserForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleRoleField = React.useCallback((field, value) => {
    setRoleForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleTogglePermission = React.useCallback((permissionId) => {
    setRoleForm((current) => ({
      ...current,
      permissionIds: current.permissionIds.includes(permissionId)
        ? current.permissionIds.filter((id) => id !== permissionId)
        : [...current.permissionIds, permissionId],
    }));
  }, []);

  const handleEditOrganization = React.useCallback((organization) => {
    if (!canManageOrganizations) {
      return;
    }

    setEditOrganizationDialog({ open: true, organization, name: organization.name });
    setErrorMessage('');
    setSuccessMessage('');
  }, [canManageOrganizations]);

  const handleCloseEditOrganizationDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setEditOrganizationDialog(resolveInitialEditOrganizationDialog());
  }, [submitting]);

  const handleEditOrganizationNameChange = React.useCallback((value) => {
    setEditOrganizationDialog((current) => ({ ...current, name: value }));
  }, []);

  const handleOpenStatusChangeDialog = React.useCallback((organization) => {
    if (!canManageOrganizations) {
      return;
    }

    setStatusChangeDialog({ open: true, organization, reason: '' });
    setErrorMessage('');
    setSuccessMessage('');
  }, [canManageOrganizations]);

  const handleCloseStatusChangeDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setStatusChangeDialog(resolveInitialStatusChangeDialog());
  }, [submitting]);

  const handleStatusChangeReasonChange = React.useCallback((value) => {
    setStatusChangeDialog((current) => ({ ...current, reason: value }));
  }, []);

  const handleEditAmsUser = React.useCallback((user) => {
    setEditingAmsUserId(user.id);
    setAmsUserForm({
      externalId: user.externalId,
      name: user.name,
      email: user.email,
      role: user.role,
      organizationId: user.organizationId,
      status: user.status,
    });
    setErrorMessage('');
    setSuccessMessage('');
    setLastGeneratedAmsPassword('');
  }, []);

  const handleEditRole = React.useCallback((role) => {
    setEditingRoleId(role.id);
    setRoleForm({
      name: role.name,
      organizationId: role.organizationId,
      permissionIds: role.permissionIds,
    });
    setErrorMessage('');
    setSuccessMessage('');
  }, []);

  // Esta tela so cria organizacoes -- editar nome e mudar status viram
  // acoes proprias (modal), cada uma com sua trilha de auditoria (ver
  // handleConfirmEditOrganization/handleConfirmOrganizationStatusChange).
  const handleSubmitOrganization = React.useCallback(async () => {
    if (!canManageOrganizations) {
      return;
    }

    if (!organizationForm.name.trim()) {
      setErrorMessage('Informe o nome da organização.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = { name: organizationForm.name.trim() };
      const createdOrganization = await createAdminOrganization(payload);

      let pendingErrorMessage = '';

      try {
        await createAccessManagementOrganization({ ...payload, externalId: createdOrganization.id });
      } catch (amsError) {
        pendingErrorMessage = `Organização criada com sucesso (ID: ${createdOrganization.id}), mas a sincronização com o AMS falhou (${getRulesEngineUiMessage(amsError)}). Edite a organização pra tentar sincronizar de novo antes de criar Usuários (AMS) para ela.`;
      }

      // loadData() reseta errorMessage no proprio inicio -- so aplicar as
      // mensagens acima depois dela rodar, ou a de erro seria apagada na hora.
      await loadData();
      resetOrganizationForm();

      if (pendingErrorMessage) {
        setErrorMessage(pendingErrorMessage);
      } else {
        setSuccessMessage(`Organização criada com sucesso. ID gerado: ${createdOrganization.id}.`);
      }
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManageOrganizations, loadData, organizationForm, resetOrganizationForm]);

  const handleConfirmEditOrganization = React.useCallback(async () => {
    const organization = editOrganizationDialog.organization;

    if (!organization || !canManageOrganizations) {
      return;
    }

    const trimmedName = editOrganizationDialog.name.trim();

    if (!trimmedName) {
      setErrorMessage('Informe o nome da organização.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await updateAdminOrganization(organization.id, { name: trimmedName });

      // Organizacoes criadas antes da sincronizacao com o AMS existir podem
      // nao ter contrapartida la ainda -- cria agora (backfill) se for o caso.
      const matchingAmsOrganization = amsOrganizations.find((item) => item.externalId === organization.id);
      let pendingErrorMessage = '';

      try {
        if (matchingAmsOrganization) {
          await updateAccessManagementOrganization(matchingAmsOrganization.amsId, { name: trimmedName });
        } else {
          await createAccessManagementOrganization({ name: trimmedName, externalId: organization.id });
        }
      } catch (amsError) {
        pendingErrorMessage = `Organização atualizada, mas a sincronização com o AMS falhou (${getRulesEngineUiMessage(amsError)}). Usuários (AMS) podem não conseguir referenciar esta organização até salvar de novo.`;
      }

      await loadData();
      setEditOrganizationDialog(resolveInitialEditOrganizationDialog());

      if (pendingErrorMessage) {
        setErrorMessage(pendingErrorMessage);
      } else {
        setSuccessMessage('Organização atualizada com sucesso.');
      }
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [amsOrganizations, canManageOrganizations, editOrganizationDialog, loadData]);

  const handleConfirmOrganizationStatusChange = React.useCallback(async () => {
    const organization = statusChangeDialog.organization;

    if (!organization || !canManageOrganizations) {
      return;
    }

    const trimmedReason = statusChangeDialog.reason.trim();

    if (!trimmedReason) {
      setErrorMessage('Informe o motivo da alteração de status.');
      return;
    }

    const activating = organization.status !== 'active';

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      if (activating) {
        await updateAdminOrganization(organization.id, { status: 'active', reason: trimmedReason });
      } else {
        await deactivateAdminOrganization(organization.id, trimmedReason);
      }

      const matchingAmsOrganization = amsOrganizations.find((item) => item.externalId === organization.id);
      if (matchingAmsOrganization) {
        try {
          await updateAccessManagementOrganization(matchingAmsOrganization.amsId, { status: activating ? 'active' : 'inactive' });
        } catch {
          // Segue mesmo se a sincronizacao falhar -- o rules-engine e' a
          // fonte de verdade principal, o status ja mudou la.
        }
      }

      await loadData();
      setStatusChangeDialog(resolveInitialStatusChangeDialog());
      setSuccessMessage(`Organização ${organization.name} ${activating ? 'ativada' : 'desativada'} com sucesso.`);
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [amsOrganizations, canManageOrganizations, loadData, statusChangeDialog]);

  const handleSubmitAmsUser = React.useCallback(async () => {
    if (editingAmsUserId ? !canManageUsersTab : !canCreateUsers) {
      return;
    }

    if (!editingAmsUserId && !creatableUserRoleOptions.includes(amsUserForm.role)) {
      setErrorMessage('O perfil selecionado não está disponível para o usuário logado.');
      return;
    }

    if (!amsUserForm.name.trim() || !amsUserForm.email.trim() || (!editingAmsUserId && !amsUserForm.externalId.trim())) {
      setErrorMessage('Informe identificador, nome e email do usuário.');
      return;
    }

    if (!['ROOT', 'SUPERADMIN'].includes(amsUserForm.role) && !amsUserForm.organizationId) {
      setErrorMessage('Selecione a organização do usuário.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    setLastGeneratedAmsPassword('');

    try {
      const payload = {
        externalId: amsUserForm.externalId.trim(),
        name: amsUserForm.name.trim(),
        email: amsUserForm.email.trim(),
        role: amsUserForm.role,
        organizationId: amsUserForm.organizationId,
        status: amsUserForm.status,
      };

      if (editingAmsUserId) {
        const updatedUser = await updateAccessManagementUser(editingAmsUserId, payload);
        setAmsUsers((current) => current.map((user) => (user.id === editingAmsUserId ? updatedUser : user)));
        setSuccessMessage('Usuário atualizado com sucesso.');
        resetAmsUserForm();
        return;
      }

      const createdUser = await createAccessManagementUser(payload);
      setAmsUsers((current) => [createdUser, ...current]);

      const generatedPassword = generateRandomSecret();

      try {
        await setAccessManagementUserCredentials(createdUser.id, generatedPassword);
        setLastGeneratedAmsPassword(generatedPassword);
        setSuccessMessage(`Usuário ${createdUser.email} criado com sucesso.`);
      } catch (credentialsError) {
        setErrorMessage(
          `O usuário ${createdUser.email} foi criado, mas não foi possível definir a senha inicial `
          + `(${getRulesEngineUiMessage(credentialsError)}). Use a ação "Definir senha" na tabela abaixo para tentar novamente.`,
        );
      }

      resetAmsUserForm();
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [amsUserForm, canCreateUsers, canManageUsersTab, creatableUserRoleOptions, editingAmsUserId, resetAmsUserForm]);

  const handleOpenUserStatusDialog = React.useCallback((user, targetStatus) => {
    if (!canManageUsersTab || !canManageTargetAmsUser(user)) {
      return;
    }

    setUserStatusDialog({ open: true, user, targetStatus, reason: '' });
    setErrorMessage('');
    setSuccessMessage('');
  }, [canManageTargetAmsUser, canManageUsersTab]);

  const handleCloseUserStatusDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setUserStatusDialog(resolveInitialUserStatusDialog());
  }, [submitting]);

  const handleUserStatusReasonChange = React.useCallback((value) => {
    setUserStatusDialog((current) => ({ ...current, reason: value }));
  }, []);

  const handleConfirmUserStatusChange = React.useCallback(async () => {
    const { user, targetStatus, reason } = userStatusDialog;

    if (!user || !canManageUsersTab || !canManageTargetAmsUser(user)) {
      return;
    }

    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setErrorMessage('Informe o motivo da alteração de status.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updatedUser = targetStatus === 'active'
        ? await activateAccessManagementUser(user.id, trimmedReason)
        : await suspendAccessManagementUser(user.id, trimmedReason);

      setAmsUsers((current) => current.map((item) => (item.id === user.id ? updatedUser : item)));
      setUserStatusDialog(resolveInitialUserStatusDialog());
      setSuccessMessage(
        targetStatus === 'active'
          ? `Usuário ${user.email} ativado com sucesso.`
          : `Usuário ${user.email} suspenso com sucesso.`,
      );
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManageTargetAmsUser, canManageUsersTab, userStatusDialog]);

  const handleOpenCredentialsDialog = React.useCallback((user) => {
    if (!canManageUsersTab || !canManageTargetAmsUser(user)) {
      return;
    }

    setCredentialsDialog({
      open: true,
      user,
      password: '',
    });
    setErrorMessage('');
    setSuccessMessage('');
    setLastGeneratedAmsPassword('');
  }, [canManageTargetAmsUser, canManageUsersTab]);

  const handleCloseCredentialsDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setCredentialsDialog({ open: false, user: null, password: '' });
  }, [submitting]);

  const handleCredentialsDialogPasswordChange = React.useCallback((value) => {
    setCredentialsDialog((current) => ({ ...current, password: value }));
  }, []);

  const handleGenerateCredentialsDialogPassword = React.useCallback(() => {
    if (!canManageUsersTab || !credentialsDialog.open) {
      return;
    }

    setCredentialsDialog((current) => ({ ...current, password: generateRandomSecret() }));
    setErrorMessage('');
    setSuccessMessage('');
  }, [canManageUsersTab, credentialsDialog.open]);

  const handleCopyCredentialsDialogPassword = React.useCallback(async () => {
    if (!credentialsDialog.password) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(credentialsDialog.password);
        setSuccessMessage('Senha copiada para a área de transferência.');
        setErrorMessage('');
      } else {
        setErrorMessage('O navegador não permite copiar automaticamente.');
      }
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error, 'Não foi possível copiar a senha.'));
    }
  }, [credentialsDialog.password]);

  const handleConfirmSetCredentials = React.useCallback(async () => {
    const user = credentialsDialog.user;

    if (!user || !canManageUsersTab || !canManageTargetAmsUser(user)) {
      return;
    }

    const resolvedPassword = String(credentialsDialog.password || '').trim() || generateRandomSecret();

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const updatedUser = await setAccessManagementUserCredentials(user.id, resolvedPassword);
      setAmsUsers((current) => current.map((item) => (item.id === user.id ? updatedUser : item)));
      setSuccessMessage(`Senha definida para ${user.email}.`);
      setLastGeneratedAmsPassword(resolvedPassword);
      setCredentialsDialog({ open: false, user: null, password: '' });
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [canManageTargetAmsUser, canManageUsersTab, credentialsDialog.password, credentialsDialog.user]);

  const handleSubmitRole = React.useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    if (!roleForm.name.trim()) {
      setErrorMessage('Informe o nome do papel.');
      return;
    }

    if (!roleForm.organizationId) {
      setErrorMessage('Selecione a organização do papel.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        name: roleForm.name.trim(),
        organizationId: roleForm.organizationId,
        permissionIds: roleForm.permissionIds,
      };

      if (editingRoleId) {
        await updateAccessManagementRole(editingRoleId, payload);
        setSuccessMessage('Papel atualizado com sucesso.');
      } else {
        await createAccessManagementRole(payload);
        setSuccessMessage('Papel criado com sucesso.');
      }

      await loadData();
      resetRoleForm();
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [editingRoleId, isAdmin, loadData, resetRoleForm, roleForm]);

  const handleOpenRoleStatusDialog = React.useCallback((role) => {
    if (!isAdmin) {
      return;
    }

    setRoleStatusDialog({ open: true, role, reason: '' });
    setErrorMessage('');
    setSuccessMessage('');
  }, [isAdmin]);

  const handleCloseRoleStatusDialog = React.useCallback(() => {
    if (submitting) {
      return;
    }

    setRoleStatusDialog(resolveInitialRoleStatusDialog());
  }, [submitting]);

  const handleRoleStatusReasonChange = React.useCallback((value) => {
    setRoleStatusDialog((current) => ({ ...current, reason: value }));
  }, []);

  const handleConfirmRoleStatusChange = React.useCallback(async () => {
    const { role, reason } = roleStatusDialog;

    if (!role || !isAdmin) {
      return;
    }

    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setErrorMessage('Informe o motivo da desativação.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await deleteAccessManagementRole(role.id, trimmedReason);
      await loadData();
      setRoleStatusDialog(resolveInitialRoleStatusDialog());
      setSuccessMessage(`Papel ${role.name} desativado com sucesso.`);
    } catch (error) {
      setErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [isAdmin, loadData, roleStatusDialog]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
          <Stack sx={{ alignItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
              Carregando gestão de organizações e usuários...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography component="h1" variant="h5">Gestão de organizações, usuários e perfis de acesso</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Centralize o cadastro de organizações, gerencie as contas de acesso ao painel e os perfis de permissão que elas usam. Administradores globais também configuram, dentro de cada organização, as fontes de dados técnicas usadas para escopar policies, regras e transações.
              </Typography>

              {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
              <SuccessToast message={successMessage} onClose={() => setSuccessMessage('')} />
              {lastGeneratedSecret ? <Alert severity="warning">Salve este segredo agora: {lastGeneratedSecret}</Alert> : null}
              {lastGeneratedAmsPassword ? <Alert severity="warning">Salve esta senha agora: {lastGeneratedAmsPassword}</Alert> : null}
              {!canManageOrganizations && !canManageUsersTab && !isAdmin ? <Alert severity="info">Seu perfil possui acesso somente leitura nesta tela.</Alert> : null}

              <Tabs value={activeTab} onChange={handleTabChange} aria-label="Gestão administrativa">
                <Tab value={TAB_VALUES.organizations} label="Organizações" />
                {canViewUsersTab ? <Tab value={TAB_VALUES.users} label="Usuários" /> : null}
                {isAdmin ? <Tab value={TAB_VALUES.roles} label="Perfis de Acesso" /> : null}
              </Tabs>
            </Stack>
          </CardContent>
        </Card>

        {activeTab === TAB_VALUES.organizations ? (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">Cadastro de organizações</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Esta seção só cadastra novas organizações. Para editar o nome ou mudar o status de uma organização existente, use as ações na tabela abaixo.
                  </Typography>

                  <FormSection title="Identificação da organização" description="Nome principal do tenant administrado.">
                    <TextField
                      fullWidth
                      label="Nome"
                      value={organizationForm.name}
                      onChange={(event) => handleOrganizationField('name', event.target.value)}
                      disabled={!canManageOrganizations || submitting}
                    />
                  </FormSection>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                    <Button variant="contained" onClick={handleSubmitOrganization} disabled={!canManageOrganizations || submitting}>
                      Criar organização
                    </Button>
                    <Button variant="outlined" onClick={resetOrganizationForm} disabled={submitting}>
                      Limpar
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">{`Organizações cadastradas (${organizations.length})`}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Acompanhe o estado atual dos tenants, a última atualização registrada e acione rapidamente edição ou mudança de status.
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell>Atualizado em</TableCell>
                          <TableCell>Última alteração</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {organizations.map((organization) => (
                          <TableRow key={organization.id}>
                            <TableCell>{organization.name}</TableCell>
                            <TableCell>{formatDateTimeHumanized(organization.updatedAt)}</TableCell>
                            <TableCell>{resolveLatestChangeSummary(organization.latestChange || organization.raw?.latest_change)}</TableCell>
                            <TableCell>{ORGANIZATION_STATUS_LABELS[organization.status] || organization.status}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                <Button size="small" variant="text" onClick={() => handleEditOrganization(organization)} disabled={!canManageOrganizations}>
                                  Editar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color={organization.status === 'active' ? 'warning' : 'success'}
                                  onClick={() => handleOpenStatusChangeDialog(organization)}
                                  disabled={!canManageOrganizations || submitting}
                                >
                                  {organization.status === 'active' ? 'Desativar' : 'Ativar'}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        {activeTab === TAB_VALUES.users && canViewUsersTab ? (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">Cadastro e edição de usuários</Typography>

                  <FormSection title="Identificação" description="Conta de acesso ao painel, gerenciada no access-management-service.">
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Identificador (external_id)"
                        value={amsUserForm.externalId}
                        onChange={(event) => handleAmsUserField('externalId', event.target.value)}
                        disabled={!canCreateUsers || Boolean(editingAmsUserId) || submitting}
                      />
                      <TextField
                        fullWidth
                        label="Nome"
                        value={amsUserForm.name}
                        onChange={(event) => handleAmsUserField('name', event.target.value)}
                        disabled={(editingAmsUserId ? !canManageUsersTab : !canCreateUsers) || submitting}
                      />
                    </Stack>
                    <TextField
                      fullWidth
                      label="Email"
                      value={amsUserForm.email}
                      onChange={(event) => handleAmsUserField('email', event.target.value)}
                      disabled={(editingAmsUserId ? !canManageUsersTab : !canCreateUsers) || submitting}
                      helperText="Usado para login no painel. A senha inicial é gerada automaticamente ao criar o usuário."
                    />
                  </FormSection>

                  <FormSection title="Escopo e perfil" description="Associe o usuário à organização correta e defina o perfil de acesso. O status é controlado pelos botões Suspender/Ativar na listagem abaixo.">
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl fullWidth disabled={(editingAmsUserId ? !canManageUsersTab : !canCreateUsers) || submitting}>
                        <InputLabel id="ams-user-organization-label">Organização</InputLabel>
                        <Select
                          labelId="ams-user-organization-label"
                          value={amsUserForm.organizationId}
                          label="Organização"
                          onChange={(event) => handleAmsUserField('organizationId', event.target.value)}
                        >
                          {availableAmsOrganizations.map((organization) => (
                            <MenuItem key={organization.id} value={organization.id}>
                              {organization.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth disabled={(editingAmsUserId ? !canManageUsersTab : !canCreateUsers) || submitting}>
                        <InputLabel id="ams-user-role-label">Perfil</InputLabel>
                        <Select
                          labelId="ams-user-role-label"
                          value={amsUserForm.role}
                          label="Perfil"
                          onChange={(event) => handleAmsUserField('role', event.target.value)}
                        >
                          {creatableUserRoleOptions.map((role) => (
                            <MenuItem key={role} value={role}>{CLIENT_ROLE_LABELS[role] || role}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </FormSection>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmitAmsUser}
                      disabled={(editingAmsUserId ? !canManageUsersTab : !canCreateUsers) || submitting}
                    >
                      {editingAmsUserId ? 'Salvar usuário' : 'Criar usuário'}
                    </Button>
                    <Button variant="outlined" onClick={resetAmsUserForm} disabled={submitting}>
                      Limpar
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">{`Usuários cadastrados (${amsUsers.length})`}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Contas com acesso ao painel, com perfil de acesso resolvido dinamicamente pelo access-management-service.
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Perfil</TableCell>
                          <TableCell>Organização</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {amsUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{asDisplayText(user.name)}</TableCell>
                            <TableCell>{asDisplayText(user.email)}</TableCell>
                            <TableCell>{CLIENT_ROLE_LABELS[user.role] || asDisplayText(user.role)}</TableCell>
                            <TableCell>{asDisplayText(amsOrganizationNameById[user.organizationId], user.organizationId || '-')}</TableCell>
                            <TableCell>{CLIENT_STATUS_LABELS[user.status] || asDisplayText(user.status)}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                <Button size="small" variant="text" onClick={() => handleEditAmsUser(user)} disabled={!canManageUsersTab || !canManageTargetAmsUser(user)}>
                                  Editar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenCredentialsDialog(user)}
                                  disabled={!canManageUsersTab || !canManageTargetAmsUser(user) || submitting}
                                >
                                  Definir senha
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => handleOpenUserStatusDialog(user, 'suspended')}
                                  disabled={!canManageUsersTab || !canManageTargetAmsUser(user) || submitting || user.status !== 'active'}
                                >
                                  Suspender
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenUserStatusDialog(user, 'active')}
                                  disabled={!canManageUsersTab || !canManageTargetAmsUser(user) || submitting || user.status === 'active'}
                                >
                                  Ativar
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        {activeTab === TAB_VALUES.roles && isAdmin ? (
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">Cadastro e edição de papéis customizados</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Papéis padrão (ROOT/SUPERADMIN/ADMIN/AUDIT/MANAGER/ANALYST/MONITOR) já vêm com um conjunto fixo de permissões e não podem ser restringidos abaixo dele — o formulário abaixo cria e edita papéis customizados, cuja lista de permissões decide o acesso de verdade.
                  </Typography>

                  <FormSection title="Identificação" description="Nome do papel e organização à qual ele pertence.">
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Nome do papel"
                        value={roleForm.name}
                        onChange={(event) => handleRoleField('name', event.target.value)}
                        disabled={!isAdmin || submitting}
                      />
                      <FormControl fullWidth disabled={!isAdmin || Boolean(editingRoleId) || submitting}>
                        <InputLabel id="role-organization-label">Organização</InputLabel>
                        <Select
                          labelId="role-organization-label"
                          value={roleForm.organizationId}
                          label="Organização"
                          onChange={(event) => handleRoleField('organizationId', event.target.value)}
                        >
                          {availableAmsOrganizations.map((organization) => (
                            <MenuItem key={organization.id} value={organization.id}>
                              {organization.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </FormSection>

                  <FormSection title="Permissões" description="Marque as combinações de recurso e ação que este papel concede.">
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Recurso</TableCell>
                            {permissionMatrixActions.map((action) => (
                              <TableCell key={action} align="center">{PERMISSION_ACTION_LABELS[action] || action}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {permissionMatrixResources.map((resource) => (
                            <TableRow key={resource}>
                              <TableCell>{PERMISSION_RESOURCE_LABELS[resource] || resource}</TableCell>
                              {permissionMatrixActions.map((action) => {
                                const permissionId = permissionIdByResourceAction[`${resource}:${action}`];

                                return (
                                  <TableCell key={action} align="center">
                                    {permissionId ? (
                                      <Checkbox
                                        checked={roleForm.permissionIds.includes(permissionId)}
                                        onChange={() => handleTogglePermission(permissionId)}
                                        disabled={!isAdmin || submitting}
                                        inputProps={{ 'aria-label': `${resource}.${action}` }}
                                      />
                                    ) : null}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </FormSection>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, pt: 0.5 }}>
                    <Button variant="contained" onClick={handleSubmitRole} disabled={!isAdmin || submitting}>
                      {editingRoleId ? 'Salvar papel' : 'Criar papel'}
                    </Button>
                    <Button variant="outlined" onClick={resetRoleForm} disabled={submitting}>
                      Limpar
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Filtre as listagens abaixo por organização — útil pra quem enxerga mais de uma.
              </Typography>
              <FormControl sx={{ minWidth: 260 }} size="small">
                <InputLabel id="role-org-filter-label">Filtrar por organização</InputLabel>
                <Select
                  labelId="role-org-filter-label"
                  value={roleFilterOrganizationId}
                  label="Filtrar por organização"
                  onChange={(event) => setRoleFilterOrganizationId(event.target.value)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {availableAmsOrganizations.map((organization) => (
                    <MenuItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">{`Papéis customizados (${filteredCustomRoles.length})`}</Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell>Organização</TableCell>
                          <TableCell>Permissões</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCustomRoles.map((role) => (
                          <TableRow key={role.id}>
                            <TableCell>{role.name}</TableCell>
                            <TableCell>{asDisplayText(amsOrganizationNameById[role.organizationId], role.organizationId || '-')}</TableCell>
                            <TableCell>{renderPermissionChips(role)}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                <Button size="small" variant="text" onClick={() => handleEditRole(role)} disabled={!isAdmin}>
                                  Editar
                                </Button>
                                <Button size="small" variant="outlined" color="warning" onClick={() => handleOpenRoleStatusDialog(role)} disabled={!isAdmin || submitting || !role.active}>
                                  Desativar
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography component="h2" variant="h6">{`Papéis padrão (referência, somente leitura) (${filteredDefaultRoles.length})`}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    As permissões abaixo já são concedidas pelo catálogo do sistema — editar um papel padrão só adiciona permissões, nunca restringe abaixo do padrão.
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell>Organização</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Permissões</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDefaultRoles.map((role) => (
                          <TableRow key={role.id}>
                            <TableCell>{CLIENT_ROLE_LABELS[role.name] || role.name}</TableCell>
                            <TableCell>{asDisplayText(amsOrganizationNameById[role.organizationId], role.organizationId || '-')}</TableCell>
                            <TableCell>{role.active ? 'Ativo' : 'Desativado'}</TableCell>
                            <TableCell>{renderPermissionChips(role)}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() => handleOpenRoleStatusDialog(role)}
                                disabled={!isAdmin || submitting || !role.active}
                              >
                                Desativar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </Stack>

      <Dialog open={editOrganizationDialog.open} onClose={handleCloseEditOrganizationDialog} fullWidth maxWidth="sm">
        <DialogTitle>Editar organização</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nome"
              value={editOrganizationDialog.name}
              onChange={(event) => handleEditOrganizationNameChange(event.target.value)}
              disabled={submitting}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseEditOrganizationDialog} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmEditOrganization} disabled={submitting}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <AuditStatusConfirmDialog
        open={statusChangeDialog.open}
        title={statusChangeDialog.organization?.status === 'active' ? 'Desativar organização' : 'Ativar organização'}
        description={
          statusChangeDialog.organization?.status === 'active'
            ? `Isso vai desativar "${statusChangeDialog.organization?.name}" e suspender as fontes de dados ativas dela. Informe o motivo para o registro de auditoria.`
            : `Isso vai reativar "${statusChangeDialog.organization?.name}". Informe o motivo para o registro de auditoria.`
        }
        reason={statusChangeDialog.reason}
        submitting={submitting}
        confirmColor={statusChangeDialog.organization?.status === 'active' ? 'warning' : 'success'}
        onReasonChange={handleStatusChangeReasonChange}
        onClose={handleCloseStatusChangeDialog}
        onConfirm={handleConfirmOrganizationStatusChange}
      />

      <AuditStatusConfirmDialog
        open={userStatusDialog.open}
        title={userStatusDialog.targetStatus === 'active' ? 'Ativar usuário' : 'Suspender usuário'}
        description={
          userStatusDialog.targetStatus === 'active'
            ? `Isso vai reativar o acesso de "${userStatusDialog.user?.email}". Informe o motivo para o registro de auditoria.`
            : `Isso vai suspender o acesso de "${userStatusDialog.user?.email}". Informe o motivo para o registro de auditoria.`
        }
        reason={userStatusDialog.reason}
        submitting={submitting}
        confirmColor={userStatusDialog.targetStatus === 'active' ? 'success' : 'warning'}
        onReasonChange={handleUserStatusReasonChange}
        onClose={handleCloseUserStatusDialog}
        onConfirm={handleConfirmUserStatusChange}
      />

      <AuditStatusConfirmDialog
        open={roleStatusDialog.open}
        title="Desativar papel"
        description={`Isso vai desativar o papel "${roleStatusDialog.role?.name}". Informe o motivo para o registro de auditoria.`}
        reason={roleStatusDialog.reason}
        submitting={submitting}
        confirmColor="warning"
        onReasonChange={handleRoleStatusReasonChange}
        onClose={handleCloseRoleStatusDialog}
        onConfirm={handleConfirmRoleStatusChange}
      />

      <Dialog open={credentialsDialog.open} onClose={handleCloseCredentialsDialog} fullWidth maxWidth="sm">
        <DialogTitle>Definir senha</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Informe uma nova senha para o usuário ou deixe em branco para gerar automaticamente.
            </Typography>
            <TextField
              fullWidth
              label="Nova senha (opcional)"
              value={credentialsDialog.password}
              onChange={(event) => handleCredentialsDialogPasswordChange(event.target.value)}
              type="password"
              autoComplete="new-password"
              disabled={submitting}
            />
            <Button
              variant="outlined"
              onClick={handleGenerateCredentialsDialogPassword}
              disabled={submitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              Gerar senha aleatória
            </Button>
            <Button
              variant="text"
              onClick={handleCopyCredentialsDialogPassword}
              disabled={submitting || !credentialsDialog.password}
              sx={{ alignSelf: 'flex-start' }}
            >
              Copiar senha
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseCredentialsDialog} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmSetCredentials} disabled={submitting}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
